import React, { useMemo, useState } from 'react';
import cs from 'classnames';
import { FormikErrors, FormikTouched } from 'formik';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { Box, Button, Flex } from 'shared/components/display';

import { BUTTON_VARIANTS } from 'shared/styles/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormButton from 'shared/components/FormButton';

import styles from './styles';

type Child = React.ReactElement<PageProps, typeof Page>;

interface Props {
  children: Child | (Child | false)[];
  Header?: React.ReactElement;
  errors?: FormikErrors<any>;
  touched?: FormikTouched<any>;
  ButtonComponent?: React.FC;
}

type PageComponent = React.FC<PageProps>;

const MultiPageLayout: React.FC<Props> & { Page: PageComponent } = ({
  children,
  Header,
  errors = {},
  touched = {},
  ButtonComponent,
}) => {
  const [activePage, setActivePage] = useState(0);

  const validChildren = useMemo(() => {
    return React.Children.toArray(children).filter((child) => React.isValidElement<PageProps>(child));
  }, [children]);

  return (
    <Box css={styles} className="multi-page-layout">
      <Box className="multi-page-layout__main-content">
        {Header && <Flex className="multi-page-layout__header">{Header}</Flex>}
        <Flex className="multi-page-layout__side-nav">
          {validChildren.map((child, index) => {
            if (React.isValidElement<PageProps>(child) && child.props.title) {
              return (
                <SideNavButton
                  key={`${child.props.title}-nav`}
                  title={child.props.title}
                  titleIcon={child.props.titleIcon}
                  fields={child.props.fields}
                  errors={errors}
                  touched={touched}
                  isActive={activePage === index}
                  onSideNavClick={() => setActivePage(index)}
                />
              );
            }
            return <React.Fragment key={`${index}-nav`}></React.Fragment>;
          })}
        </Flex>
        {validChildren.map((child, index) => {
          if (React.isValidElement<PageProps>(child) && child.props.title) {
            const extraProps: Partial<PageProps> = {
              isActive: activePage === index,
              onNext: () => setActivePage(index + 1),
              onPrevious: () => setActivePage(index - 1),
              key: `${child.props.title}-page`,
              ButtonComponent,
              className: !Boolean(Header) ? 'multi-page-layout__grid-content--no-header' : '',
            };
            return React.cloneElement(child, extraProps);
          }
          return <React.Fragment key={`${index}-page`}>{child}</React.Fragment>;
        })}
      </Box>
    </Box>
  );
};

export interface PageProps {
  title: string;
  titleIcon?: any;
  isActive?: boolean;
  children: React.ReactElement;
  isLastPage?: boolean;
  isFirstPage?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  fields?: string[];
  key?: string;
  ButtonComponent?: React.FC;
  className?: string;
}

const Page: React.FC<PageProps> = ({
  isActive = false,
  children,
  isLastPage = false,
  isFirstPage = false,
  onNext,
  onPrevious,
  key,
  ButtonComponent = FormButton,
  className = '',
}) => {
  if (!isActive) {
    return <></>;
  }

  return (
    <Box key={key} css={styles} className={cs('multi-page-layout__grid-content', className)}>
      <Flex className="multi-page-layout__page-content">
        <Box>{children}</Box>
        <Flex className="multi-page-layout__btn-wrapper">
          {!isFirstPage && (
            <ButtonComponent
              type="button"
              onClick={onPrevious}
              variant={BUTTON_VARIANTS.SECONDARY_SMALL}
              className="multi-page-layout__prev-btn"
            >
              Previous
            </ButtonComponent>
          )}
          {!isLastPage && (
            <ButtonComponent
              type="button"
              onClick={onNext}
              variant={BUTTON_VARIANTS.PRIMARY_SMALL}
              className="multi-page-layout__next-btn"
            >
              Next
            </ButtonComponent>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

type SideNavButtonProps = Pick<PageProps, 'fields' | 'title' | 'titleIcon'> & {
  errors: FormikErrors<any>;
  touched: FormikTouched<any>;
  onSideNavClick?: () => void;
  isActive: boolean;
};

const SideNavButton: React.FC<SideNavButtonProps> = ({
  fields = [],
  onSideNavClick,
  title,
  titleIcon,
  isActive,
  errors,
  touched,
}) => {
  const hasError = useMemo(() => {
    return fields.some((field) => errors[field] && touched[field]);
  }, [errors, touched, fields]);

  return (
    <Button
      css={styles}
      type="button"
      className={cs('multi-page-layout__side-nav-item', 'endWithEllipsis', {
        'multi-page-layout__side-nav-item--active': isActive,
        'multi-page-layout__side-nav-item--error': hasError,
      })}
      onClick={onSideNavClick}
      variant={BUTTON_VARIANTS.PLAIN}
      dataCy={`multi-page-layout__side-nav-item-${title}`}
    >
      {titleIcon && <FontAwesomeIcon icon={titleIcon} className="multi-page-layout__title-icon" fixedWidth />}
      <span className="multi-page-layout__title">{title}</span>
      {hasError && <FontAwesomeIcon icon={faCircleExclamation} className="multi-page-layout__error-icon" fixedWidth />}
    </Button>
  );
};

MultiPageLayout.Page = Page;

export default MultiPageLayout;
